import { useState, useEffect } from "react";
import api from "../api";
import { useTheme } from "../components/Theme";

const ORANGE = "#E8520A";
const STAGES = [
  { id: "prospeccao", label: "Prospecção", color: "#6366f1" },
  { id: "contato",    label: "Contato",    color: "#f59e0b" },
  { id: "reuniao",    label: "Reunião",    color: "#3b82f6" },
  { id: "proposta",   label: "Proposta",   color: "#8b5cf6" },
  { id: "fechado",    label: "Fechado",    color: "#10b981" },
  { id: "perdido",    label: "Perdido",    color: "#ef4444" },
];

function fmt(v) { return `R$${Number(v||0).toLocaleString("pt-BR")}`; }

export default function Dashboard() {
  const { theme } = useTheme();
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leads/metricas").then(r=>{setData(r.data);setLoading(false);}).catch(()=>setLoading(false));
  }, []);

  if (loading) return <div style={{ color:theme.textMuted, padding:40, textAlign:"center" }}>Carregando métricas...</div>;
  if (!data)   return <div style={{ color:"#ef4444", padding:40, textAlign:"center" }}>Erro ao carregar dados.</div>;

  const pipeline = data.pipeline || [];
  const origens  = data.origens  || [];
  const mrrFechado   = pipeline.find(p=>p._id==="fechado")?.valor||0;
  const mrrPipeline  = pipeline.filter(p=>!["fechado","perdido"].includes(p._id)).reduce((a,p)=>a+p.valor,0);
  const totalLeads   = pipeline.reduce((a,p)=>a+p.count,0);
  const totalFechado = pipeline.find(p=>p._id==="fechado")?.count||0;
  const totalPerdido = pipeline.find(p=>p._id==="perdido")?.count||0;
  const taxaConv = totalLeads ? Math.round((totalFechado/totalLeads)*100) : 0;

  const pipelineChartData = STAGES.map(s=>{
    const found = pipeline.find(p=>p._id===s.id)||{valor:0,count:0};
    return { label:s.label, color:s.color, value:found.valor, count:found.count };
  });
  const maxPipeline = Math.max(...pipelineChartData.map(d=>d.value),1);
  const origensPieData = origens.slice(0,6).map((o,i)=>({...o, color:["#6366f1","#E8520A","#10b981","#f59e0b","#3b82f6","#8b5cf6"][i%6]}));
  const totalOrigens = origens.reduce((a,o)=>a+o.count,0);
  const size=140,cx=70,cy=70,r=50,stroke=18,circ=2*Math.PI*r;
  let offset=0;
  const slices = origensPieData.map(d=>{
    const pct=totalOrigens?d.count/totalOrigens:0;
    const slice={...d,pct,dasharray:circ,dashoffset:circ-pct*circ,rotate:offset*360};
    offset+=pct; return slice;
  });

  return (
    <div style={{ padding:24, fontFamily:"'Barlow',sans-serif", background:theme.bg, minHeight:"100vh" }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:0, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22, letterSpacing:2, color:theme.text }}>DASHBOARD</h2>
        <p style={{ margin:"4px 0 0", fontSize:13, color:theme.textMuted }}>Visão geral da Agência Optimus</p>
      </div>

      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:24 }}>
        {[["MRR Fechado",fmt(mrrFechado),"#10b981","receita mensal recorrente"],["Pipeline",fmt(mrrPipeline),ORANGE,"potencial em negociação"],["Total de Leads",totalLeads,theme.text,"todos os estágios"],[`Taxa de Conv.`,`${taxaConv}%`,"#6366f1",`${totalFechado} fechados`],["Perdidos",totalPerdido,"#ef4444","oportunidades perdidas"]].map(([label,val,color,sub])=>(
          <div key={label} style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"16px 20px", flex:1, minWidth:140, boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.06)":"none" }}>
            <div style={{ fontSize:11, color:theme.textMuted, letterSpacing:".04em", textTransform:"uppercase", marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:26, fontWeight:800, color, fontFamily:"'Barlow Condensed',sans-serif" }}>{val}</div>
            <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>{sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:24 }}>
        <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20, boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.06)":"none" }}>
          <h3 style={{ margin:"0 0 16px", fontSize:13, fontWeight:600, color:theme.textSub, letterSpacing:".04em", textTransform:"uppercase" }}>Pipeline por Etapa</h3>
          {pipelineChartData.map(({label,value,color,count})=>(
            <div key={label} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                <span style={{ color:theme.textSub }}>{label}</span>
                <span style={{ color, fontWeight:700 }}>{fmt(value)} <span style={{ color:theme.textMuted }}>({count})</span></span>
              </div>
              <div style={{ background:theme.bgInput, borderRadius:6, height:10, overflow:"hidden" }}>
                <div style={{ width:`${maxPipeline?(value/maxPipeline)*100:0}%`, background:color, height:"100%", borderRadius:6 }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20, boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.06)":"none" }}>
          <h3 style={{ margin:"0 0 16px", fontSize:13, fontWeight:600, color:theme.textSub, letterSpacing:".04em", textTransform:"uppercase" }}>Origem dos Leads</h3>
          <div style={{ display:"flex", alignItems:"center", gap:20 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke={theme.bgInput} strokeWidth={stroke}/>
              {slices.map((s,i)=>(
                <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={stroke} strokeDasharray={`${s.dasharray}`} strokeDashoffset={s.dashoffset} transform={`rotate(${s.rotate*1-90} ${cx} ${cy})`} />
              ))}
              <text x={cx} y={cy-6} textAnchor="middle" fill={theme.text} fontSize="20" fontWeight="800" fontFamily="Barlow Condensed,sans-serif">{totalOrigens}</text>
              <text x={cx} y={cy+10} textAnchor="middle" fill={theme.textMuted} fontSize="10">leads</text>
            </svg>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {slices.map((s,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12 }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:s.color, flexShrink:0 }}/>
                  <span style={{ color:theme.textSub }}>{s._id}</span>
                  <span style={{ color:s.color, fontWeight:700, marginLeft:"auto" }}>{Math.round(s.pct*100)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20, boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.06)":"none" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <h3 style={{ margin:0, fontSize:13, fontWeight:600, color:theme.textSub, letterSpacing:".04em", textTransform:"uppercase" }}>Meta Anual — 500 Clientes</h3>
          <span style={{ color:"#10b981", fontWeight:700, fontSize:13 }}>{totalFechado}/500</span>
        </div>
        <div style={{ background:theme.bgInput, borderRadius:8, height:14, overflow:"hidden", marginBottom:8 }}>
          <div style={{ width:`${Math.min((totalFechado/500)*100,100)}%`, background:`linear-gradient(90deg,${ORANGE},#10b981)`, height:"100%", borderRadius:8 }}/>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:theme.textMuted }}>
          <span>0</span>
          <span>{Math.round((totalFechado/500)*100)}% da meta</span>
          <span>500</span>
        </div>
      </div>
    </div>
  );
}
